import { HttpError } from '@/lib/https';
import type { SegmentBlockingCampaignApi } from '../types/segments-api.types';

export const SEGMENT_RULE_DEPTH_MESSAGE =
  'Segments support at most two levels of grouping. Remove nested groups inside a group and try again.';

export const SEGMENT_PREVIEW_TRUNCATED_MESSAGE =
  'This rule is taking too long to count in full — the numbers below are a lower bound. Try adding a condition to narrow it.';

export const parseSegmentFieldErrors = (errors: string[] | null | undefined): Record<string, string[]> => {
  const fieldErrors: Record<string, string[]> = {};
  for (const entry of errors ?? []) {
    const i = entry.indexOf(': ');
    if (i < 0) {
      (fieldErrors._generic ??= []).push(entry);
      continue;
    }
    const field = entry.slice(0, i);
    const message = entry.slice(i + 2);
    (fieldErrors[field] ??= []).push(message);
  }
  return fieldErrors;
};

export const SEGMENT_DUPLICATE_NAME_MESSAGE =
  'A segment with this name already exists. Segment names must be unique.';

export const SEGMENT_SESSION_REAUTH_MESSAGE = 'Please sign in again and retry.';

export const getSegmentHttpStatus = (error: unknown): number | undefined => {
  if (error instanceof HttpError) return error.status;
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: number }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
};

export const getSegmentSessionGuidance = (error: unknown): string | undefined =>
  getSegmentHttpStatus(error) === 500 ? SEGMENT_SESSION_REAUTH_MESSAGE : undefined;

export const isSegmentNotFoundError = (error: unknown): boolean =>
  getSegmentHttpStatus(error) === 404;

/** ASP.NET ValidationProblemDetails depth error — path like `$.rules.groups[0].groups`. */
export const isSegmentRuleDepthError = (problem: unknown): boolean => {
  if (typeof problem !== 'object' || problem === null || !('errors' in problem)) {
    return false;
  }

  const errors = (problem as { errors: unknown }).errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return false;
  }

  return Object.keys(errors as Record<string, unknown>).some((key) =>
    /\.groups\[\d+\]\.groups$/.test(key)
  );
};

export const getBlockingCampaignsFromError = (
  error: unknown
): SegmentBlockingCampaignApi[] | null => {
  if (!(error instanceof HttpError) || error.status !== 409) return null;
  const data = (error.error as { data?: { blockingCampaigns?: SegmentBlockingCampaignApi[] } })
    ?.data;
  return Array.isArray(data?.blockingCampaigns) ? data.blockingCampaigns : null;
};

/** Create/rename duplicate-name 409 — empty `errors`, sentence lives in `message`. */
export const getDuplicateSegmentNameError = (error: unknown): string | undefined => {
  if (!(error instanceof HttpError) || error.status !== 409) return undefined;
  if (getBlockingCampaignsFromError(error)) return undefined;

  // The server's `message` can be just the status word ("Conflict"); only trust a real sentence.
  const body = error.error as { message?: string };
  const message = typeof body?.message === 'string' ? body.message.trim() : '';
  if (message && /\s/.test(message) && !/^conflict$/i.test(message)) {
    return message;
  }
  return SEGMENT_DUPLICATE_NAME_MESSAGE;
};

export const getFieldErrorsFromUnknown = (error: unknown): Record<string, string[]> => {
  if (!(error instanceof HttpError)) return {};

  const duplicateName = getDuplicateSegmentNameError(error);
  if (duplicateName) {
    return { Name: [duplicateName] };
  }

  if (isSegmentRuleDepthError(error.error)) {
    return { Rules: [SEGMENT_RULE_DEPTH_MESSAGE] };
  }

  const body = error.error as { errors?: string[] | Record<string, string[] | string> };
  if (Array.isArray(body?.errors)) {
    return parseSegmentFieldErrors(body.errors);
  }

  if (body?.errors && typeof body.errors === 'object') {
    const fieldErrors: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(body.errors)) {
      const messages = Array.isArray(value) ? value : [String(value)];
      const field = key.includes('rules') || key.startsWith('$.rules') ? 'Rules' : key;
      (fieldErrors[field] ??= []).push(...messages);
    }
    return fieldErrors;
  }

  return {};
};

export const extractQuotedFieldFromRulesMessage = (message: string): string | null => {
  const match = message.match(/'([^']+)'/);
  return match?.[1] ?? null;
};

/** `Rule group 2 needs at least one condition.` → 0-based index into `rules.groups`. */
export const extractRuleGroupIndexFromMessage = (message: string): number | null => {
  const match = message.match(/^Rule group (\d+)\b/i);
  if (!match) return null;
  const oneBased = Number(match[1]);
  if (!Number.isInteger(oneBased) || oneBased < 1) return null;
  return oneBased - 1;
};
